// Languages.jsx
import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router';

import { getLanguageTranslationKey } from '../resource/getCurrentLanguage.js';
import { EMPTY_ARRAY } from '../resource/defaults.js';
import { TranslationNamespace } from '../resource/translationNamespace.js';
import {
  DEFAULT_LANGUAGE,
  LANGUAGE_QUERY_NAME,
} from '../resource/i18nCommon.js';
import { generateLangPath } from '../resource/langPath.js';

import media from '../style/media.js';

import swagLogo from '../../img/img-swag-icon.svg';
import { color } from '../style/variables.js';

export const Languages = ({
  supportedLanguages = EMPTY_ARRAY,
  nextUrl = '',
}) => {
  const { t } = useTranslation();
  // Carry the entrance query (utm / affiliate / everflow params) over to the
  // destination page. The language links are full page loads, so anything left
  // out here is gone for good — the X-Track snippet on the next page reads
  // tracking params from `window.location.search` only.
  const { search } = useLocation();
  const [countdown, setCountdown] = useState(5);
  const timerRef = useRef(null);

  useEffect(() => {
    if (!nextUrl) return;

    timerRef.current = setInterval(() => {
      setCountdown(prev => prev - 1);
    }, 1000);

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [nextUrl]);

  useEffect(() => {
    if (!nextUrl || countdown !== 0) return;

    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }

    const url = new URL(nextUrl, window.location.origin);
    new URLSearchParams(window.location.search).forEach((value, key) => {
      if (!url.searchParams.has(key)) url.searchParams.append(key, value);
    });
    url.searchParams.set(LANGUAGE_QUERY_NAME, DEFAULT_LANGUAGE);
    window.location.replace(url.href);
  }, [countdown, nextUrl]);

  return (
    <StyledLanguages>
      <LogoContainer>
        <img src={swagLogo} alt="swag.live logo" />
      </LogoContainer>

      <Description>
        SWAG — Live creators, premium shows, and exclusive content.
      </Description>

      <Separator />

      {nextUrl ? (
        <RedirectMessage>
          <MessageTitle>
            Language not supported, redirecting to English version
          </MessageTitle>
          <CountdownText>Redirecting in {countdown} seconds...</CountdownText>
        </RedirectMessage>
      ) : (
        <LanguageList>
          <LanguageTitle>Choose your language</LanguageTitle>
          {supportedLanguages.map(language => (
            <LanguageItem key={language}>
              <a
                href={`${generateLangPath('/', language)}${search}`}
                className="language-link"
                hrefLang={language}
                rel="alternate"
                title={t(getLanguageTranslationKey({ language }), {
                  ns: TranslationNamespace.SETTINGS,
                })}
                aria-label={t(getLanguageTranslationKey({ language }), {
                  ns: TranslationNamespace.SETTINGS,
                })}
              >
                {t(getLanguageTranslationKey({ language }), {
                  ns: TranslationNamespace.SETTINGS,
                })}
              </a>
            </LanguageItem>
          ))}
        </LanguageList>
      )}
    </StyledLanguages>
  );
};

Languages.propTypes = {
  supportedLanguages: PropTypes.array,
  nextUrl: PropTypes.string,
};

const StyledLanguages = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;

  padding-top: 48px;

  ${media.tablet`
    padding-top: 16px;
  `}
`;

const LogoContainer = styled.div`
  padding-bottom: 24px;
  & img {
    height: 52px;
  }

  ${media.tablet`
    padding-bottom: 16px;
    & img {
      height: 48px;
    }
  `}
`;

const Description = styled.div`
  color: ${color.white};
  font-size: 14px;
  font-weight: 400;
  line-height: 150%;
  letter-spacing: 0.014px;
  text-align: center;

  padding: 0 24px 24px;

  ${media.tablet`
    padding: 0 16px 16px;
  `}
`;

const Separator = styled.hr`
  width: 100%;
  max-width: 608px;

  margin: 0;
  height: 1px;
  border: none;
  background-color: #646464;
  margin: 0;

  ${media.tablet`
    max-width: 296px;
  `}
`;

const LanguageList = styled.nav`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 16px;
  margin: 0 16px;
  gap: 24px;

  ${media.tablet`
    gap: 16px;
  `}
`;

const LanguageTitle = styled.div`
  color: #646464;
  font-size: 14px;
  font-weight: 400;
  line-height: 150%;
  letter-spacing: 0.014px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const LanguageItem = styled.div`
  color: ${color.neutral['100_alpha_80']};
  text-align: center;
  font-size: 16px;
  font-weight: 400;
  line-height: 150%;
  letter-spacing: 0.016px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const RedirectMessage = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 32px 24px;
  gap: 16px;
`;

const MessageTitle = styled.div`
  color: ${color.white};
  font-size: 18px;
  font-weight: 500;
  line-height: 150%;
  text-align: center;

  ${media.tablet`
    font-size: 16px;
  `}
`;

const CountdownText = styled.div`
  color: ${color.neutral['100_alpha_60']};
  font-size: 16px;
  font-weight: 400;
  line-height: 150%;
  text-align: center;

  ${media.tablet`
    font-size: 14px;
  `}
`;

export default Languages;
